import { ENDPOINTS, getEndpoint } from 'utils/api';
import { FileUploadType } from 'utils/types';

import Api from './Api';
import { parseDate } from 'utils/date';

export default class FileUpload {
    id?: string;
    transactionId?: string | number;
    policyNumber?: string;
    fileName?: string;
    content?: string | ArrayBuffer;
    contentType?: string;
    timestamp?: Date;
    fileType: FileUploadType = FileUploadType.UNDERWRITER;

    public fileToBase64 = file =>
        new Promise((resolve, reject) => {
            this.fileName = file.name;
            this.contentType = file.type;

            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => {
                if (reader.result !== null) this.content = reader.result.toString().split(',')[1];
                resolve(reader.result);
            };

            reader.onerror = error => reject(error);
        });

    public transformFrom = data => {
        this.id = data.Id;
        this.fileName = data.FileName;
    };

    public transformFromAttachment = data => {
        this.id = data.Id;
        this.transactionId = data.transactionId;
        this.contentType = data.Type;
        this.fileName = `${data.Title}.${data.Type}`;
        this.timestamp = parseDate(data.Added);
        this.fileType = data.AttachmentType.toLowerCase();
    };

    public transformFromRemittanceFile = data => {
        this.id = data.remittanceId;
        this.fileType = FileUploadType.REMITTANCE_FILE;
        this.fileName = data.attachmentFileName ? data.attachmentFileName : `${this.id}.pdf`
    };

    public transformCsvFromRemittance = data => {
        this.id = data.remittanceId;
        this.fileType = FileUploadType.REMITTANCE_CSV;
        this.fileName = `${this.id}.csv`;
    };

    public transformFromClaim = data => {
        this.id = data.id;
        this.policyNumber = data.policyNumber;
        this.fileType = FileUploadType.CLAIMS_REPORT;
        this.fileName = `${this.policyNumber}.pdf`;
    };

    public download = (returnRaw = false) => {
        const abortController = new AbortController();

        const fileName = this.fileName ? encodeURIComponent(this.fileName).replaceAll('(', '%28').replaceAll(')', '%29') : '';

        const params = {
            id: this.id,
            transactionId: this.transactionId,
            fileName: fileName,
            fileExtension: this.contentType,
            policyNumber: this.policyNumber
        };

        let endpointStr = '';
        let setFilename = false;

        switch (this.fileType) {
            case FileUploadType.UNDERWRITER:
            case FileUploadType.BROKER:
                endpointStr = ENDPOINTS.BrokerCorrespondenceDownloadAttachment;
                break;

            case FileUploadType.REMITTANCE_FILE:
                endpointStr = ENDPOINTS.RemittanceAdviceFileDownload;
                break;

            case FileUploadType.REMITTANCE_CSV:
                endpointStr = ENDPOINTS.RemittanceAdviceCsvDownload;
                break;

            case FileUploadType.CLAIMS_REPORT:
                endpointStr = ENDPOINTS.ClaimsReportDownload;
                setFilename = true;
                break;
        }

        const endpoint = getEndpoint(endpointStr, params, false);

        const promise = new Promise<FileUpload>((resolve, reject) => {
            Api.get(endpoint, abortController, { responseType: 'arraybuffer' }, true)
                .then((result: any) => {

                    if (returnRaw) {
                        this.content = result.data as ArrayBuffer;
                    } else {
                        const data = new Uint8Array(result.data as ArrayBufferLike);
                        // @ts-ignore
                        const raw = String.fromCharCode.apply(null, data);
                        const base64 = btoa(raw);
                        this.content = base64;
                    }
                    if(setFilename) {
                        this.fileName = `${result.headers['content-disposition'].split('filename=')[1]}.pdf`;
                    }
                    resolve(this);
                })
                .catch(error => {
                    reject(error);
                });
        });

        return { promise, abortController };
    };

    public upload = onUploadProgress => {
        const abortController = new AbortController();
        const endpoint = ENDPOINTS.UploadFile;

        const postData = {
            FileName: this.fileName,
            ContentBase64: this.content,
            ContentType: this.contentType,
        };

        const options = {
            onUploadProgress: onUploadProgress,
        };

        const promise = new Promise<FileUpload>((resolve, reject) => {
            Api.post(endpoint, postData, abortController, options)
                .then(result => {
                    this.transformFrom(result);
                    resolve(this);
                })
                .catch(() => {
                    reject([]);
                });
        });

        return { promise, abortController };
    };

    public uploadMultipart = (file, onUploadProgress) => {
        const abortController = new AbortController();

        let endpoint = ENDPOINTS.BankStatementUpload;
        let parameter = 'File';
        if(this.fileType === FileUploadType.REMITTANCE_FILE) {
            endpoint = ENDPOINTS.RemittanceAdviceUpload;
        }else if(this.fileType === FileUploadType.BULK_POLICY_UPDATE_CSV) {
            endpoint = ENDPOINTS.TransferPolicyCSVUpload;
            parameter = 'upload_file';
        }else if(this.fileType === FileUploadType.WELCOME_EMAIL_CSV) {
            endpoint = ENDPOINTS.WelcomeEmailCSVUpload;
            parameter = 'upload_file';
        }else if(this.fileType === FileUploadType.BULK_RENEWAL_ACTION) {
            endpoint = ENDPOINTS.RenewalBulkAction;
        }else if(this.fileType === FileUploadType.UNDERWRITER) {
            endpoint = getEndpoint(ENDPOINTS.UploadInternalAttachment, {transactionId: this.transactionId});
        }

        const options = {
            onUploadProgress: onUploadProgress,
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        };

        const formData = new FormData();
        formData.append(parameter, file);

        const promise = new Promise((resolve, reject) => {
            Api.post(endpoint, formData, abortController, options)
                .then(result => {
                    resolve(result);
                })
                .catch(e => {
                    reject(e);
                });
        });

        return { promise, abortController };
    };

    public contentBase64 = () => {
        return `data:${this.contentType};base64,${this.content}`;
    };
}
